import { Slot, SlotFillProvider, withFilters }              from '@wordpress/components';
import { __ }                                               from '@wordpress/i18n';
import { Formik, Form }                                     from 'formik';
import React                                                from 'react';
import cfwConvertOptionsObjectToArray                       from '../../../functions/cfwConvertOptionsObjectToArray';
import SettingsFormContainerPropsInterface                  from '../../../interfaces/SettingsFormContainerPropsInterface';
import SettingsFormPropsPlanInterface                       from '../../../interfaces/SettingsFormPropsPlanInterface';
import AdminPageSection                                     from '../AdminPageSection';
import CheckboxField                                        from '../Fields/CheckboxField';
import ColorPickerField                                     from '../Fields/ColorPickerField';
import IconRadioGroupRowField                               from '../Fields/IconRadioGroupRowField';
import NumberField                                          from '../Fields/NumberField';
import TextField                                            from '../Fields/TextField';
import ToggleCheckboxField                                  from '../Fields/ToggleCheckboxField';
import WPMediaUploadButton                                  from '../Fields/WPMediaUploadButton';
import LockedFieldWrapper                                   from '../LockedFieldWrapper';
import SevereAlert                                          from '../SevereAlert';

interface SideCartSettingsInterface {
    enable_side_cart: boolean;
    side_cart_icon: string;
    side_cart_custom_icon_attachment_id: number|null;
    side_cart_icon_color: string;
    side_cart_icon_width: number;
    enable_floating_cart_button: boolean;
    floating_cart_button_right_position: number;
    floating_cart_button_bottom_position: number;
    hide_floating_cart_button_empty_cart: boolean;
    shake_floating_cart_button: boolean;
    enable_ajax_add_to_cart: boolean;
    enable_side_cart_payment_buttons: boolean;
    enable_order_bumps_on_side_cart: boolean;
    enable_side_cart_suggested_products: boolean;
    side_cart_suggested_products_heading: string;
    enable_side_cart_suggested_products_random_fallback: boolean;
    allow_side_cart_item_variation_changes: boolean;
    show_side_cart_item_discount: boolean;
    enable_promo_codes_on_side_cart: boolean;
    enable_side_cart_totals: boolean;
    enable_side_cart_continue_shopping_button: boolean;
    enable_free_shipping_progress_bar: boolean;
    side_cart_free_shipping_threshold: number;
    side_cart_amount_remaining_message: string;
    side_cart_free_shipping_message: string;
    side_cart_free_shipping_progress_indicator_color: string;
    side_cart_free_shipping_progress_bg_color: string;
    enable_free_shipping_progress_bar_at_checkout: boolean;
}

interface SideCartFormWooCommerceSettingsInterface {
    thank_you_order_statuses: any;
}

interface SideCartFormParamsInterface {
    icon_options: any[];
    default_free_shipping_progress_bar_color: string;
    custom_icon_preview_url: string;
}

interface SideCartSettingsFormPropsInterface extends SettingsFormContainerPropsInterface {
    settings: SideCartSettingsInterface;
    woocommerce_settings: SideCartFormWooCommerceSettingsInterface;
    plan: SettingsFormPropsPlanInterface;
    params: SideCartFormParamsInterface;
}

const SideCartSettingsForm: React.FC<SideCartSettingsFormPropsInterface> = ( props ) => {
    const { saveSettings, isLoading, searchTerm } = props;

    const AdditionalSettings = withFilters(
        'CheckoutWC.Admin.Settings.Pages.SideCart',
    )( () => <></> );

    return (
        <SlotFillProvider>
            <AdditionalSettings />
            <Formik
                initialValues={{
                    ...props.settings,
                }}
                enableReinitialize={true}
                onSubmit={async ( values ) => {
                    await saveSettings( values );
                }}
            >
                {( { values } ) => (
                    <Form className={'space-y-6 transition-all'} style={{ filter: isLoading ? 'blur(2px)' : 'none' }}>
                        <AdminPageSection
                            title={__( 'Side Cart', 'checkout-wc' )}
                            description={__( 'Configure the Side Cart.', 'checkout-wc' )}
                            content={
                                <>
                                    <LockedFieldWrapper plan={props.plan} slug={'side-cart'} locked={props.plan.plan_level < 2} requiredPlans={props.plan.labels.required_list[ 2 ]}>
                                        <ToggleCheckboxField
                                            name='enable_side_cart'
                                            label={__( 'Enable Side Cart', 'checkout-wc' )}
                                            description={__( 'Replace your cart page with a beautiful side cart that slides in from the right when items are added to the cart.', 'checkout-wc' )}
                                            disabled={props.plan.plan_level < 2}
                                            searchTerm={searchTerm}
                                        />
                                    </LockedFieldWrapper>

                                    <Slot name="CheckoutWC.Admin.Pages.SideCart.Options" />
                                </>
                            }
                        />

                        {props.plan.plan_level >= 2 && values.enable_side_cart
                            && (
                                <>
                                    <AdminPageSection
                                        title={__( 'Side Cart Icon', 'checkout-wc' )}
                                        description={__( 'Used by the Side Cart and Floating Side Cart Button.', 'checkout-wc' )}
                                        content={
                                            <>
                                                <div className={'flex space-x-4'}>
                                                    <div className={'grow space-y-4 cfw-admin-section-component-content'}>
                                                        <IconRadioGroupRowField
                                                            name='side_cart_icon'
                                                            label={__( 'Icon', 'checkout-wc' )}
                                                            description={__( 'Choose the Side Cart icon.', 'checkout-wc' )}
                                                            options={cfwConvertOptionsObjectToArray( props.params.icon_options )}
                                                            searchTerm={searchTerm}
                                                        />

                                                        <WPMediaUploadButton
                                                            name='side_cart_custom_icon_attachment_id'
                                                            label={__( 'Custom Icon', 'checkout-wc' )}
                                                            description={__( 'Upload a custom icon. Overrides the icon selection above. SVG REQUIRED.', 'checkout-wc' )}
                                                            defaultUrl={props.params.custom_icon_preview_url}
                                                            searchTerm={searchTerm}
                                                        />

                                                        <NumberField
                                                            name='side_cart_icon_width'
                                                            label={__( 'Width', 'checkout-wc' )}
                                                            description={__( 'The width of the icon in pixels. Default: 34', 'checkout-wc' )}
                                                            searchTerm={searchTerm}
                                                        />
                                                    </div>

                                                    <ColorPickerField
                                                        name='side_cart_icon_color'
                                                        label={__( 'Icon Color', 'checkout-wc' )}
                                                        defaultValue='#222'
                                                        searchTerm={searchTerm}
                                                    />
                                                </div>
                                            </>
                                        }
                                    />

                                    <AdminPageSection
                                        title={__( 'Suggested Products', 'checkout-wc' )}
                                        description={__( 'Configure Suggested Products cross-sells.', 'checkout-wc' )}
                                        content={
                                            <>
                                                <CheckboxField
                                                    name="enable_side_cart_suggested_products"
                                                    label={__( 'Enable Suggested Products', 'checkout-wc' )}
                                                    description={__( 'Display cross sells / suggested products at the bottom of the side cart.', 'checkout-wc' )}
                                                    searchTerm={searchTerm}
                                                />

                                                { values.enable_side_cart_suggested_products
                                                    && (
                                                        <>
                                                            <TextField
                                                                name='side_cart_suggested_products_heading'
                                                                label={__( 'Heading', 'checkout-wc' )}
                                                                description={__( 'Heading above the suggested products. Leave blank for default. Default: You may also like&hellip;', 'checkout-wc' )}
                                                                placeholder={__( 'You may also like…', 'checkout-wc' )}
                                                                searchTerm={searchTerm}
                                                            />

                                                            <CheckboxField
                                                                name="enable_side_cart_suggested_products_random_fallback"
                                                                label={__( 'Enable Random Cross Sells Fallback', 'checkout-wc' )}
                                                                description={__( 'If none of the cart products have cross sell items defined, display random products instead.', 'checkout-wc' )}
                                                                searchTerm={searchTerm}
                                                            />
                                                        </>
                                                    )
                                                }
                                            </>
                                        }
                                    />

                                    <AdminPageSection
                                        title={__( 'Floating Side Cart Button', 'checkout-wc' )}
                                        description={__( 'Configure the Floating Side Cart Button', 'checkout-wc' )}
                                        content={
                                            <>
                                                <CheckboxField
                                                    name="enable_floating_cart_button"
                                                    label={__( 'Enable Floating Cart Button', 'checkout-wc' )}
                                                    description={__( 'Enable floating cart button on the bottom right of pages.', 'checkout-wc' )}
                                                    searchTerm={searchTerm}
                                                />

                                                { values.enable_floating_cart_button
                                                    && (
                                                        <>
                                                            <div className={'flex space-x-4 cfw-admin-section-component-content'}>
                                                                <NumberField
                                                                    name='floating_cart_button_right_position'
                                                                    label={__( 'Right Position', 'checkout-wc' )}
                                                                    description={__( 'The position from the right side of the screen in pixels. Default: 20', 'checkout-wc' )}
                                                                    searchTerm={searchTerm}
                                                                />

                                                                <NumberField
                                                                    name='floating_cart_button_bottom_position'
                                                                    label={__( 'Bottom Position', 'checkout-wc' )}
                                                                    description={__( 'The position from the bottom of the screen in pixels. Default: 20', 'checkout-wc' )}
                                                                    searchTerm={searchTerm}
                                                                />
                                                            </div>

                                                            <CheckboxField
                                                                name="hide_floating_cart_button_empty_cart"
                                                                label={__( 'Hide Button If Empty Cart', 'checkout-wc' )}
                                                                description={__( 'Hide floating cart button if cart is empty.', 'checkout-wc' )}
                                                                searchTerm={searchTerm}
                                                            />
                                                        </>
                                                    )
                                                }
                                            </>
                                        }
                                    />

                                    <AdminPageSection
                                        title={__( 'Free Shipping Progress Bar', 'checkout-wc' )}
                                        description={__( 'Configure the Free Shipping Progress Bar', 'checkout-wc' )}
                                        content={
                                            <>
                                                <CheckboxField
                                                    name="enable_free_shipping_progress_bar"
                                                    label={__( 'Enable Free Shipping Progress Bar', 'checkout-wc' )}
                                                    description={__( 'Enable Free Shipping progress bar to show customers how close they are to obtaining free shipping. Uses your shipping settings to determine limits. To override, specify amount below.', 'checkout-wc' )}
                                                    searchTerm={searchTerm}
                                                />

                                                {values.enable_free_shipping_progress_bar
                                                    && (
                                                        <>
                                                            <NumberField
                                                                name='side_cart_free_shipping_threshold'
                                                                label={__( 'Free Shipping Threshold', 'checkout-wc' )}
                                                                description={__( 'Cart subtotal required to qualify for free shipping. To use automatic detection based on shipping configuration, leave blank. Enter in store base currency.', 'checkout-wc' )}
                                                                searchTerm={searchTerm}
                                                            />

                                                            <TextField
                                                                name='side_cart_amount_remaining_message'
                                                                label={__( 'Amount Remaining Message', 'checkout-wc' )}
                                                                placeholder={
                                                                    // translators: %s: amount remaining for free shipping
                                                                    __( "You're %s away from free shipping!", 'checkout-wc' )
                                                                }
                                                                description={
                                                                    // translators: %s: amount remaining for free shipping
                                                                    __( "The amount remaining to qualify for free shipping message. Leave blank for default. Default: You're %s away from free shipping!", 'checkout-wc' )
                                                                }
                                                                searchTerm={searchTerm}
                                                            />

                                                            {values.side_cart_amount_remaining_message && !values.side_cart_amount_remaining_message.includes( '%s' ) && (
                                                                <SevereAlert
                                                                    description={
                                                                        // translators: %s: placeholder for amount remaining
                                                                        __( 'Please ensure your amount remaining message includes the %s placeholder which is replaced with amount remaining when displayed to customers.', 'checkout-wc' )
                                                                    }
                                                                />
                                                            )}

                                                            <TextField
                                                                name='side_cart_free_shipping_message'
                                                                label={__( 'Free Shipping Message', 'checkout-wc' )}
                                                                placeholder={__( 'Congrats! You get free standard shipping.', 'checkout-wc' )}
                                                                description={__( 'The free shipping message. Leave blank for default. Default: Congrats! You get free standard shipping.', 'checkout-wc' )}
                                                                searchTerm={searchTerm}
                                                            />

                                                            <CheckboxField
                                                                name="enable_free_shipping_progress_bar_at_checkout"
                                                                label={__( 'Enable Free Shipping Progress Bar At Checkout', 'checkout-wc' )}
                                                                description={__( 'Enable Free Shipping Progress Bar on the checkout page cart summary.', 'checkout-wc' )}
                                                                searchTerm={searchTerm}
                                                            />

                                                            <div className={'flex space-x-4 cfw-admin-section-component-content'}>
                                                                <ColorPickerField
                                                                    name='side_cart_free_shipping_progress_indicator_color'
                                                                    label={__( 'Progress Indicator Color', 'checkout-wc' )}
                                                                    defaultValue={props.params.default_free_shipping_progress_bar_color}
                                                                    searchTerm={searchTerm}
                                                                />

                                                                <ColorPickerField
                                                                    name='side_cart_free_shipping_progress_bg_color'
                                                                    label={__( 'Progress Bar Background Color', 'checkout-wc' )}
                                                                    defaultValue='#f5f5f5'
                                                                    searchTerm={searchTerm}
                                                                />
                                                            </div>
                                                        </>
                                                    )
                                                }
                                            </>
                                        }
                                    />

                                    {props.plan.plan_level >= 2 && values.enable_side_cart
                                        && (
                                            <AdminPageSection
                                                title={__( 'Options', 'checkout-wc' )}
                                                description={__( 'Control various Side Cart options.', 'checkout-wc' )}
                                                content={
                                                    <>
                                                        <CheckboxField
                                                            name="shake_floating_cart_button"
                                                            label={__( 'Disable Cart Auto Open', 'checkout-wc' )}
                                                            description={__( 'Instead of opening the side cart, gently shake the floating cart button (if visible) to indicate a successful add to cart event.', 'checkout-wc' )}
                                                            searchTerm={searchTerm}
                                                        />

                                                        <CheckboxField
                                                            name="enable_ajax_add_to_cart"
                                                            label={__( 'Enable AJAX Add to Cart', 'checkout-wc' )}
                                                            description={__( 'Use AJAX on archive and single product pages to add items to cart. By default, WooCommerce requires a full form submit with page reload. Enabling this option uses AJAX to add items to the cart.', 'checkout-wc' )}
                                                            searchTerm={searchTerm}
                                                        />

                                                        <CheckboxField
                                                            name="enable_side_cart_payment_buttons"
                                                            label={__( 'Enable Express Payment Buttons', 'checkout-wc' )}
                                                            description={__( 'Enable express payment buttons from gateways that support the WooCommerce Minicart.', 'checkout-wc' )}
                                                            searchTerm={searchTerm}
                                                        />

                                                        <CheckboxField
                                                            name="enable_order_bumps_on_side_cart"
                                                            label={__( 'Enable Order Bumps', 'checkout-wc' )}
                                                            description={__( 'Enable order bumps that are set to display below cart items to appear in side cart.', 'checkout-wc' )}
                                                            searchTerm={searchTerm}
                                                        />

                                                        <CheckboxField
                                                            name="allow_side_cart_item_variation_changes"
                                                            label={__( 'Allow Variation Changes', 'checkout-wc' )}
                                                            description={__( 'Displays an edit link under cart items that allows customers to change which variation is selected in the cart.', 'checkout-wc' )}
                                                            searchTerm={searchTerm}
                                                        />

                                                        <CheckboxField
                                                            name="show_side_cart_item_discount"
                                                            label={__( 'Enable Sale Prices', 'checkout-wc' )}
                                                            description={__( 'Enable sale price under on cart item labels in side cart. Example: <s>$10.00</s> $5.00', 'checkout-wc' )}
                                                            searchTerm={searchTerm}
                                                        />

                                                        <CheckboxField
                                                            name="enable_promo_codes_on_side_cart"
                                                            label={__( 'Enable Coupons', 'checkout-wc' )}
                                                            description={__( 'Enable customers to apply coupons from the side cart.', 'checkout-wc' )}
                                                            searchTerm={searchTerm}
                                                        />

                                                        {values.enable_promo_codes_on_side_cart && (
                                                            <CheckboxField
                                                                name="enable_side_cart_coupon_code_link"
                                                                label={__( 'Hide Coupon Code Field Behind Link', 'checkout-wc' )}
                                                                description={__( 'Initially hide coupon field until "Have a coupon code?" link is clicked.', 'checkout-wc' )}
                                                                searchTerm={searchTerm}
                                                            />
                                                        )}

                                                        <CheckboxField
                                                            name="enable_side_cart_totals"
                                                            label={__( 'Show Shipping and Totals', 'checkout-wc' )}
                                                            description={__( 'Enable customers to see shipping and order total in addition to subtotal.', 'checkout-wc' )}
                                                            searchTerm={searchTerm}
                                                        />

                                                        <CheckboxField
                                                            name="enable_side_cart_continue_shopping_button"
                                                            label={__( 'Enable Continue Shopping Button', 'checkout-wc' )}
                                                            description={__( 'Enable Continue Shopping Button at bottom of Side Cart. Disabled by default.', 'checkout-wc' )}
                                                            searchTerm={searchTerm}
                                                        />
                                                    </>
                                                }
                                            />
                                        )
                                    }
                                </>
                            )
                        }

                        <button className={'cfw_admin_page_submit hidden'} type="submit">Submit</button>
                    </Form>
                )}
            </Formik>
        </SlotFillProvider>
    );
};

export default SideCartSettingsForm;
